import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Mail } from 'lucide-react';
import axios from 'axios';
import Login from '../components/Login';
import Register from '../components/Register';
import BrandLogo from '../components/BrandLogo';
import Toast from '../components/Toast';
import { authService } from '../services/authService';
import API_CONFIG from '../config/api';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from '../i18n/LocaleContext';

// Wanderlog-style auth: language top-centre, big wordmark, social pills at the
// bottom; email sign-up / sign-in open as their own full-screen pages.
const AuthPage = ({ onAuthSuccess }) => {
  const { t, language } = useTranslation();
  const [view, setView] = useState('main'); // main | login | register
  const [toast, setToast] = useState(null);
  const [cc, setCc] = useState(null);

  // 欢迎语按 IP 归属国家展示;失败静默降级为不带国家
  useEffect(() => {
    let alive = true;
    axios.get(`${API_CONFIG.getApiUrl(API_CONFIG.ENDPOINTS.AUTH)}/geo`)
      .then(({ data }) => { if (alive && data.cc) setCc(data.cc); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const countryName = useMemo(() => {
    if (!cc) return '';
    try { return new Intl.DisplayNames([language], { type: 'region' }).of(cc) || ''; }
    catch (e) { return cc; }
  }, [cc, language]);

  const handleLogin = async (credentials) => {
    const response = await authService.login(credentials);
    onAuthSuccess(response.user);
  };

  const handleRegister = async (userData) => {
    const response = await authService.register(userData);
    // Delay 1.5s để user thấy toast trước khi chuyển trang
    setTimeout(() => {
      onAuthSuccess(response.user);
    }, 1500);
  };

  const soon = () => setToast({ type: 'warning', message: t('socialComingSoon') });

  const toastNode = toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />;

  if (view !== 'main') {
    // 品牌蓝渐变头部 + 上叠白色圆角卡片
    return (
      <div className="min-h-screen bg-canvas flex flex-col">
        {toastNode}
        <div className="bg-brand-500">
          <div className="max-w-md mx-auto w-full px-5 pt-5 pb-24">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setView('main')}
                aria-label={t('back')}
                className="-ml-1.5 flex items-center gap-0.5 py-1 text-[15px] font-semibold text-white"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={2.4} />
                {t('back')}
              </button>
              <LanguageSelector className="text-white" />
            </div>
            <h1 className="mt-16 pb-2 text-[32px] font-extrabold text-white leading-[1.15]">
              {view === 'login' ? t('authWelcomeBack') : t('authWelcomeNew')}
              {countryName && (
                <>
                  <br />
                  {t('friendFrom', { country: countryName })}
                </>
              )}
            </h1>
          </div>
        </div>
        <div className="flex-1 -mt-12 bg-white rounded-t-2xl">
          <div className="max-w-md mx-auto w-full px-5 pt-8 pb-10">
            <BrandLogo size="sm" />
            <h2 className="mt-6 text-[21px] font-extrabold text-ink">
              {view === 'login' ? t('login') : t('register')}
            </h2>
            <div className="mt-6">
              {view === 'login' ? (
                <Login onLogin={handleLogin} onSwitchToRegister={() => setView('register')} />
              ) : (
                <Register onRegister={handleRegister} onSwitchToLogin={() => setView('login')} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const socialButtons = [
    { key: 'facebook', label: t('signUpWithFacebook'), icon: FacebookIcon },
    { key: 'google', label: t('signUpWithGoogle'), icon: GoogleIcon },
    { key: 'apple', label: t('signUpWithApple'), icon: AppleIcon },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col pb-10">
      {toastNode}

      <div className="max-w-md mx-auto w-full px-5 flex-1 flex flex-col">
        <div className="flex-1" />

        <BrandLogo />

        <div className="flex-1 min-h-[96px]" />

        <div className="space-y-3">
          {socialButtons.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={soon}
              className="relative w-full h-14 rounded-xl bg-white border border-black/[0.08] flex items-center justify-center text-[15px] font-bold text-ink active:bg-canvas transition-colors"
            >
              <span className="absolute left-5 flex w-6 h-6 items-center justify-center">
                <Icon />
              </span>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          <button
            type="button"
            onClick={() => setView('register')}
            className="relative w-full h-14 rounded-xl bg-white border border-black/[0.08] flex items-center justify-center text-[15px] font-bold text-ink active:bg-canvas transition-colors"
          >
            <span className="absolute left-5 flex w-6 h-6 items-center justify-center">
              <Mail className="w-[22px] h-[22px] text-ink" strokeWidth={1.9} />
            </span>
            {t('signUpWithEmail')}
          </button>
        </div>

        <p className="mt-5 text-center text-[13.5px] text-ink-secondary">
          {t('alreadyHaveAccount')}{' '}
          <button
            type="button"
            onClick={() => setView('login')}
            className="font-extrabold text-ink"
          >
            {t('signIn')}
          </button>
        </p>
      </div>
    </div>
  );
};

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1877F2" aria-hidden="true">
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.273c0-.851-.076-1.67-.218-2.455H12v4.642h6.458a5.52 5.52 0 0 1-2.394 3.622v3.011h3.878c2.269-2.089 3.578-5.165 3.578-8.82z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.956-1.075 7.942-2.907l-3.878-3.011c-1.075.72-2.45 1.16-4.064 1.16-3.125 0-5.771-2.111-6.715-4.951H1.29v3.109A11.995 11.995 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.285 14.291A7.213 7.213 0 0 1 4.909 12c0-.784.136-1.547.376-2.291V6.6l-4-3.109A11.995 11.995 0 0 0 0 12c0 1.94.466 3.773 1.29 5.355l3.995-3.064z" />
      <path fill="#EA4335" d="M12 4.75c1.768 0 3.35.608 4.598 1.804l3.442-3.442C17.951 1.182 15.235 0 12 0A11.995 11.995 0 0 0 1.29 6.6l3.995 3.109C6.229 6.861 8.875 4.75 12 4.75z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-[22px] h-[22px]" fill="#0B0B0B" aria-hidden="true">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.702" />
    </svg>
  );
}

export default AuthPage;
